<script setup lang="ts">
import { cn } from '@/lib/utils'
import { CheckIcon } from '@radix-icons/vue'
import { Loader } from 'lucide-vue-next'
import { computed, nextTick, onMounted, ref } from 'vue'

const props = withDefaults(
    defineProps<{
        placeholder?: string
        modelValue?: string | null
        disabled?: boolean
        saving?: boolean
        loading?: boolean
        saved?: boolean
        label?: string
        focus?: boolean
        lowercase?: boolean
    }>(),
    {
        type: 'text',
        disabled: false,
        saving: false,
        loading: false,
        saved: false,
        focus: false,
        lowercase: false,
    }
)

const emits = defineEmits<{
    (e: 'update:modelValue', payload: string | undefined | null): void
}>()

const classes =
    'flex items-center w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground has-focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50'

const value = computed({
    get() {
        if (props.modelValue === '') {
            return null
        }

        return props.modelValue
    },
    set(value) {
        if (value === '') {
            value = null
        }

        if (props.lowercase) {
            value = value?.toLowerCase()
        }

        emits('update:modelValue', value)
    },
})

const target = ref()

onMounted(() => {
    nextTick(() => {
        if (props.focus) {
            target.value.focus()
        }
    })
})
</script>

<template>
    <div :class="cn(classes, $attrs.class ?? '')">
        <div class="w-full">
            <div v-if="label" class="flex items-center mb-2 font-semibold">
                <div class="flex-1">{{ label }}</div>
                <Loader
                    v-if="saving === true || loading === true"
                    class="h-4 w-4 shrink-0 opacity-50 animate-spin"
                />
                <CheckIcon
                    v-if="saved === true && saving === false"
                    class="h-4 w-4 shrink-0 opacity-50 text-green-600"
                />
            </div>
            <div class="flex items-center">
                <input
                    ref="target"
                    v-model="value"
                    type="text"
                    :placeholder="placeholder"
                    :disabled="disabled || saving || loading"
                    class="flex-1 py-[3px] text-black"
                    style="width: inherit"
                />
                <Loader
                    v-if="(saving === true || loading === true) && label === undefined"
                    class="h-4 w-4 shrink-0 opacity-50 animate-spin"
                />
                <CheckIcon
                    v-if="saved === true && saving === false && label === undefined"
                    class="h-4 w-4 shrink-0 opacity-50 text-green-600"
                />
            </div>
        </div>
    </div>
</template>
